import { Button, DismissIcon, StarFilledIcon } from '@iheartjane/reefer';
import { clsx } from 'clsx';

import {
  useBloomFilters,
  useProductSearchFilters,
  useSearchParamFilters,
} from '@jane/bloom/hooks/filters';
import { isRootCategoryFilterGroup } from '@jane/bloom/hooks/filters/isRootCategoryFilterGroup';
import type { ProductSearchFiltersViewModel } from '@jane/bloom/viewModels/productSearchFilters';

import styles from './selectedFilterTags.module.css';

export const SelectedFilterTags = () => {
  const { sortedDisplayFilterKeys } = useBloomFilters();
  const { getProductSearchFilterOptionLabel, selectedCategory } =
    useProductSearchFilters();

  const filtersNotInDropDowns = [
    'thcPotencies',
    'cbdPotencies',
    'searchText',
    'hasBrandDiscount',
  ];

  const filtersToManage = [
    ...sortedDisplayFilterKeys,
    ...filtersNotInDropDowns,
  ];

  const {
    clearFilter,
    clearFilters,
    getFilterRangeValue,
    getSelectedFilterValues,
    setFilter,
  } = useSearchParamFilters(...filtersToManage);

  let selectedFilterValues = [];

  if (selectedCategory !== undefined) {
    selectedFilterValues = getSelectedFilterValues().filter(
      (group) => !isRootCategoryFilterGroup(group)
    );
  } else {
    selectedFilterValues = getSelectedFilterValues();
  }

  // Jane Gold is managed by its own toggle button, it's hidden from the pill list
  // while still allowing clearFilters() to remove it.
  selectedFilterValues = selectedFilterValues.filter(
    (group) => group.filterKey !== 'hasBrandDiscount'
  );

  return (
    <div
      className={clsx(styles['selectedFilterTagsContainer'], {
        [styles['nonEmptyFilterTags']]: selectedFilterValues.length > 0,
      })}
      data-testid="selected-filters"
    >
      {selectedFilterValues.map(({ filterKey, filterValues }) => {
        switch (filterKey) {
          case 'thcPotencies': {
            const thcRange = getFilterRangeValue('thcPotencies');
            return (
              <Button
                variant="secondary"
                ariaLabel="thcPotencies"
                key="thcPotencies"
                startIcon={<DismissIcon color="inherit" size="sm" />}
                label={`THC: ${thcRange?.min}-${thcRange?.max}%`}
                onClick={() => {
                  clearFilter('thcPotencies');
                }}
              />
            );
          }
          case 'cbdPotencies': {
            const cbdRange = getFilterRangeValue('cbdPotencies');
            return (
              <Button
                variant="secondary"
                ariaLabel="cbdPotencies"
                key="cbdPotencies"
                startIcon={<DismissIcon color="inherit" size="sm" />}
                label={`CBD: ${cbdRange?.min}-${cbdRange?.max}%`}
                onClick={() => {
                  clearFilter('cbdPotencies');
                }}
              />
            );
          }
          case 'ratings': {
            return filterValues.map((filter) => {
              const filterLabel = getProductSearchFilterOptionLabel(
                filterKey,
                filter
              );
              return (
                <Button
                  variant="secondary"
                  ariaLabel={filter}
                  key={filter}
                  startIcon={
                    <>
                      <DismissIcon color="inherit" size="sm" />
                      <StarFilledIcon size="sm" color="gold" />
                    </>
                  }
                  label={`${filterLabel} and up`}
                  onClick={() => setFilter('ratings', filter, false)}
                />
              );
            });
          }
          case 'searchText': {
            return (
              <Button
                variant="secondary"
                ariaLabel="searchText"
                key="searchText"
                startIcon={<DismissIcon color="inherit" size="sm" />}
                label={`${filterValues[0]}`}
                onClick={() => {
                  clearFilter('searchText');
                }}
              />
            );
          }
          default: {
            return filterValues.map((filter) => {
              return (
                <Button
                  variant="secondary"
                  ariaLabel={filter}
                  key={filter}
                  startIcon={<DismissIcon color="inherit" size="sm" />}
                  label={getProductSearchFilterOptionLabel(
                    filterKey as keyof ProductSearchFiltersViewModel,
                    filter
                  )}
                  onClick={() => setFilter(filterKey, filter, false)}
                />
              );
            });
          }
        }
      })}

      {selectedFilterValues.length > 0 && (
        <div
          className={clsx(
            styles['productFilterDeselectAllFiltersContainer'],
            'focus-visible'
          )}
          tabIndex={0}
          role="button"
          onClick={() => {
            clearFilters();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              clearFilters();
            }
          }}
        >
          Clear filters
        </div>
      )}
    </div>
  );
};
